import type { ReactNode } from "react";
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { API_BASE } from "../config";
import { track } from "../track";
import type { Currency, Language, PreviewDoc } from "../types";
import type { Words } from "./copy";
import { fill } from "./multiBuy";
import { PageContext } from "./page";

/** Express delivery priced for the address they type, above the payment form
 *  (server: EXPRESS_LIVE_PRICING, delivery.expressLive). Free delivery is the
 *  default and costs them nothing: the Stripe form is there for it with no
 *  step added. Someone who asks for express types an address here, sees what
 *  it costs to that address, and chooses it; the form then opens for express
 *  to that address alone, with the address already in it. The server prices
 *  it again when the session is made and refuses a number that moved.
 *
 *  Used by the one-piece sheet and by "Order them together", through
 *  useExpress, which holds the state the sheet needs (is the form hidden, what
 *  does the form ask for) and the component that shows it. */

/** What the checkout is asked for: the address they typed and the price they
 *  were shown for it, in the currency's cents. */
export interface ExpressPick {
  country: string;
  city: string;
  postcode: string;
  line1: string;
  /** The recipient's full name, which the form opens with. */
  name: string;
  cents: number;
}

type Answer = { state: "ok"; cents: number; price: string; remote: boolean } | { state: "none" } | { state: "error" };
/** An answer kept with what it was asked for, so it is only ever used for that
 *  address, that size and that many pieces. */
type Quote = (Extract<Answer, { state: "ok" }> & { at: Typed; sizeKey: string; units: number }) | { state: "none" } | { state: "error" };
interface Typed {
  name: string;
  country: string;
  line1: string;
  postcode: string;
  city: string;
}
interface Chosen {
  cents: number;
  price: string;
  remote: boolean;
  /** The size and the number of pieces it was priced for: another size or
   *  another count is priced again, with no typing. */
  sizeKey: string;
  units: number;
  at: Typed;
}

/** What they typed and chose, for the life of the page and no longer: in
 *  memory only, never in storage, because it is where they live. Another
 *  page (token) starts clean. */
let memory: { token: string; typed: Typed; chosen: Chosen | null } | null = null;

/** How long a changed size or count waits before the price is asked again. */
const REQUOTE_MS = 350;

const BY_LANGUAGE: Partial<Record<Language, string>> = { pt: "PT", nl: "NL", de: "DE", fr: "FR", es: "ES", it: "IT" };
/** The countries the picker lists when the server does not send its own list
 *  (delivery.expressCountries, which is every country the checkout ships to).
 *  The server refuses any it does not ship to, which the page words as "not
 *  available". */
const FALLBACK_COUNTRIES = (
  "AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE " +
  "GB CH NO IS LI US CA AU NZ JP KR SG HK TW AE IL TR MX BR IN"
).split(" ");

/** Countries whose addresses have no postcode, so the postcode is optional
 *  there. A copy of NO_POSTCODE_COUNTRIES in functions/src/lib/expressPrice.ts
 *  (Stripe's own list; Ireland is not in it, Eircodes exist). */
export const NO_POSTCODE_COUNTRIES: readonly string[] = [
  "AE", "AG", "AO", "AW", "BF", "BI", "BJ", "BO", "BS", "BW", "BZ", "CD", "CF", "CG", "CI", "CK", "CM", "DJ", "DM", "ER",
  "FJ", "GD", "GH", "GM", "GQ", "GY", "HK", "JM", "KE", "KI", "KM", "KN", "KP", "LC", "ML", "MO", "MR", "MW", "NR", "NU",
  "PA", "QA", "RW", "SB", "SC", "SL", "SO", "SR", "ST", "SY", "TF", "TK", "TL", "TO", "TT", "TV", "UG", "VU", "YE", "ZW",
];

/** The country of the browser's own region ("en-GB" is GB), when its language
 *  is the page's: someone reading Portuguese in a Brazilian browser is most
 *  likely in Brazil. */
function browserCountry(language: Language): string {
  try {
    const base = language === "zh-hans" ? "zh" : language;
    for (const tag of navigator.languages?.length ? navigator.languages : [navigator.language]) {
      const m = /^([a-z]{2,3})(?:-[A-Za-z]{4})?-([A-Za-z]{2})$/.exec(tag ?? "");
      if (m && m[1] === base) return m[2]!.toUpperCase();
    }
  } catch {
    /* No navigator: the page's language decides. */
  }
  return "";
}

/** Where the page most likely ships to: for English the country of its
 *  currency, else the browser's region in the page's language, else its
 *  language's country. Empty when it cannot tell, or when the checkout does
 *  not ship there. */
function defaultCountry(language: Language, currency: Currency, countries: string[]): string {
  const priced = language === "en" ? (currency === "GBP" ? "GB" : currency === "USD" ? "US" : "") : "";
  const likely = priced || browserCountry(language) || BY_LANGUAGE[language] || "";
  return countries.includes(likely) ? likely : "";
}

/** The postcode is the only field that can be left empty, and only where the
 *  country has none. */
const postcodeOptional = (country: string) => NO_POSTCODE_COUNTRIES.includes(country);
const complete = (t: Typed) =>
  !!(t.name.trim() && t.country && t.line1.trim() && t.city.trim() && (t.postcode.trim() || postcodeOptional(t.country)));

/** The largest of several sizes ("6 cm", "10 cm"), the one the courier charges
 *  most for: what the server quotes a payment of several by. */
function largest(sizes: string[]): string {
  let best = sizes[0] ?? "";
  for (const s of sizes) if (parseFloat(s) > parseFloat(best) || !(parseFloat(best) >= 0)) best = s;
  return best;
}

/** What the server's refusals of an express session mean for the choice:
 *  express_repriced (a new price to look at), express_unavailable (no express
 *  to that address, only), express_off (live pricing was switched off while
 *  the page was open) and express_error (the price could not be had just now:
 *  the pick stays and they can try again). */
export type ExpressRefusal =
  | { error: "express_repriced"; cents: number; price: string; remote?: boolean }
  | { error: "express_unavailable" }
  | { error: "express_off" }
  | { error: "express_error" };

export interface ExpressState {
  enabled: boolean;
  /** The express the form should ask for, or undefined for free delivery. */
  pick: ExpressPick | undefined;
  /** The address fields are open, or a price is being checked again: the pay
   *  form waits (hidden, not rebuilt) until they have chosen. */
  hold: boolean;
  /** For the form's key: a new session when this moves, and only then. While
   *  the choice is being changed the last express key is kept, so choosing
   *  the same address and price again opens no new session. */
  key: string;
  /** The server refused the express in the session: back to the choice with
   *  the new price, to free delivery, or to try again. */
  refused: (why: ExpressRefusal) => void;
  // Internals the component draws from.
  sizeKey: string;
  units: number;
  open: boolean;
  asking: boolean;
  busy: boolean;
  /** The price could not be had when the form asked: the pick stays. */
  errored: boolean;
  quote: Quote | null;
  notice: "" | "repriced" | "unavailable" | "error";
  typed: Typed;
  chosen: Chosen | null;
  language: Language;
  currency: Currency;
  /** Every country the checkout ships to. */
  countries: string[];
  setField: (k: keyof Typed, v: string) => void;
  openIt: () => void;
  see: () => void;
  use: () => void;
  change: () => void;
  keepFree: () => void;
  retry: () => void;
}

/** The sign-in header the studio's look sends, as PayForm does: signed out,
 *  nothing is asked. */
async function adminHeaders(): Promise<Record<string, string> | null> {
  const { auth } = await import("../admin");
  await auth.authStateReady();
  const idToken = await auth.currentUser?.getIdToken();
  return idToken ? { Authorization: `Bearer ${idToken}` } : null;
}

/** Asks the server what express costs to this address for this size and this
 *  many pieces. */
async function quoteFor(token: string, language: Language, admin: boolean, size: string, units: number, at: Typed): Promise<Answer> {
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (admin) {
      const signed = await adminHeaders();
      if (!signed) return { state: "error" };
      Object.assign(headers, signed);
    }
    const r = await fetch(`${API_BASE}/api/express-quote`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token, country: at.country, city: at.city.trim(), postcode: at.postcode.trim(), size, units, language }),
    });
    // A country the checkout does not ship to is a plain "not here"; any other
    // refusal (a bad address or request) is an error, not an answer.
    if (r.status === 400) {
      const body = (await r.json().catch(() => null)) as { error?: string } | null;
      return body?.error === "bad-country" ? { state: "none" } : { state: "error" };
    }
    if (!r.ok) return { state: "error" };
    const data = (await r.json()) as { state?: string; price?: string; cents?: number; remote?: boolean };
    if (data.state === "ok" && typeof data.cents === "number" && data.price) {
      return { state: "ok", cents: data.cents, price: data.price, remote: !!data.remote };
    }
    return data.state === "none" ? { state: "none" } : { state: "error" };
  } catch {
    return { state: "error" };
  }
}

/** The express choice of a sheet. Call it at the top level of the sheet with
 *  the sizes of what is being bought (one, or every line) and how many pieces
 *  there are in all; draw it with <ExpressChoice>; give the form `pick`, key
 *  it with `key`, hide it while `hold`, and pass `refused` for the server's
 *  express refusals. */
export function useExpress(delivery: PreviewDoc["delivery"] | null | undefined, sizes: string[], units = 1): ExpressState {
  const page = useContext(PageContext);
  const token = page?.token ?? "";
  const language: Language = page?.language ?? "en";
  const currency: Currency = page?.currency ?? "EUR";
  const admin = !!page?.admin;
  const enabled = !!delivery?.expressLive && !!token;
  const countries = delivery?.expressCountries?.length ? delivery.expressCountries : FALLBACK_COUNTRIES;
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errored, setErrored] = useState(false);
  const [retries, setRetries] = useState(0);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [notice, setNotice] = useState<ExpressState["notice"]>("");
  const latest = useRef(0);
  const sizeNow = useRef("");
  const unitsNow = useRef(1);
  const lastKey = useRef("");
  const requote = useRef(0);

  if (enabled && (!memory || memory.token !== token)) {
    memory = {
      token,
      typed: { name: page?.name ?? "", country: defaultCountry(language, currency, countries), line1: "", postcode: "", city: "" },
      chosen: null,
    };
  }
  const mem = enabled ? memory : null;
  const typed: Typed = mem?.typed ?? { name: "", country: "", line1: "", postcode: "", city: "" };
  const chosen = mem?.chosen ?? null;
  const sizeKey = largest(sizes);
  const count = Math.min(Math.max(1, Math.round(units) || 1), 20);
  sizeNow.current = sizeKey;
  unitsNow.current = count;
  /** Priced for this very parcel. */
  const fits = (c: { sizeKey: string; units: number }) => c.sizeKey === sizeKey && c.units === count;

  const setField = useCallback((k: keyof Typed, v: string) => {
    if (!memory) return;
    memory.typed = { ...memory.typed, [k]: v };
    // The name does not change the price: a price already shown stays.
    if (k !== "name") {
      // A question still out was asked about the old address: its answer is
      // dropped when it comes.
      latest.current++;
      setAsking(false);
      setQuote(null);
    }
    bump();
  }, []);

  const see = useCallback(() => {
    if (!memory || !complete(memory.typed)) return;
    const mine = ++latest.current;
    const at = memory.typed;
    setAsking(true);
    setQuote(null);
    setNotice("");
    const asked = sizeKey;
    const askedUnits = count;
    void quoteFor(token, language, admin, asked, askedUnits, at).then((a) => {
      if (mine !== latest.current) return;
      setAsking(false);
      // The size or the count moved while it was out: that answer is for
      // another parcel.
      if (sizeNow.current !== asked || unitsNow.current !== askedUnits) return;
      const q: Quote = a.state === "ok" ? { ...a, at, sizeKey: asked, units: askedUnits } : a;
      setQuote(q);
      track("express.quoted", {
        state: q.state,
        country: at.country,
        ...(q.state === "ok" ? { remote: q.remote, cents: q.cents } : {}),
      });
    });
  }, [token, language, admin, sizeKey, count]);

  const use = useCallback(() => {
    if (!memory || quote?.state !== "ok") return;
    // Only a price for this very address, size and count is ever used. The
    // name is not part of the price.
    const t = memory.typed;
    const same = (Object.keys(t) as (keyof Typed)[]).every((k) => k === "name" || t[k] === quote.at[k]);
    if (!same || quote.sizeKey !== sizeKey || quote.units !== count) {
      setQuote(null);
      return;
    }
    memory.chosen = { cents: quote.cents, price: quote.price, remote: quote.remote, sizeKey, units: count, at: { ...memory.typed } };
    setOpen(false);
    setQuote(null);
    setNotice("");
    setErrored(false);
    track("express.chosen");
    bump();
  }, [quote, sizeKey, count]);

  const change = useCallback(() => {
    if (!memory) return;
    const was = memory.chosen;
    memory.chosen = null;
    setErrored(false);
    // The fields come back as they were typed, and the price they were shown
    // is there to use again at once.
    setQuote(
      was && was.sizeKey === sizeKey && was.units === count
        ? { state: "ok", cents: was.cents, price: was.price, remote: was.remote, at: { ...was.at }, sizeKey: was.sizeKey, units: was.units }
        : null
    );
    setOpen(true);
    bump();
  }, [sizeKey, count]);

  const keepFree = useCallback(() => {
    if (memory) memory.chosen = null;
    latest.current++;
    setAsking(false);
    setOpen(false);
    setQuote(null);
    setErrored(false);
    bump();
  }, []);

  const openIt = useCallback(() => {
    setOpen(true);
    setNotice("");
    track("express.opened");
  }, []);

  const retry = useCallback(() => {
    setErrored(false);
    setRetries((n) => n + 1);
  }, []);

  const refused = useCallback<ExpressState["refused"]>((why) => {
    if (!memory) return;
    // The price could not be had just now: nothing is wrong with the choice.
    // It stays, with a way to ask again (retry makes a new session).
    if (why.error === "express_error") {
      setErrored(true);
      return;
    }
    const was = memory.chosen;
    memory.chosen = null;
    if (why.error === "express_repriced" && was) {
      // Back to the choice, with the new price to use or leave.
      setQuote({ state: "ok", cents: why.cents, price: why.price, remote: why.remote === true, at: { ...was.at }, sizeKey: was.sizeKey, units: was.units });
      setOpen(true);
      setNotice("repriced");
    } else {
      setQuote(null);
      setOpen(false);
      setNotice("unavailable");
    }
    setErrored(false);
    // Live pricing switched off under an open page: the page reads itself
    // again and stops offering the choice.
    if (why.error === "express_off") void page?.refresh();
    bump();
  }, [page]);

  // Another size or count is another parcel: priced again for the address
  // already typed, without asking them for it. The form waits meanwhile.
  const chosenSize = chosen?.sizeKey ?? "";
  const chosenUnits = chosen?.units ?? 0;
  const chosenAt = chosen?.at;
  useEffect(() => {
    const run = ++requote.current;
    // No sizes (a sheet opened with nothing in it yet) is not another size: the
    // choice made elsewhere stays as it was.
    if (!enabled || !sizeKey || !memory?.chosen || !chosenAt || (chosenSize === sizeKey && chosenUnits === count)) {
      setBusy(false);
      return;
    }
    const mine = ++latest.current;
    setBusy(true);
    // A moment after the last tap on + or − or a size, so a run of taps is one
    // question to the server and not one each.
    const timer = window.setTimeout(() => {
      void quoteFor(token, language, admin, sizeKey, count, chosenAt).then((q) => {
        if (run !== requote.current || mine !== latest.current || !memory) return;
        setBusy(false);
        if (q.state === "ok") {
          memory.chosen = { cents: q.cents, price: q.price, remote: q.remote, sizeKey, units: count, at: chosenAt };
        } else {
          memory.chosen = null;
          setNotice(q.state === "none" ? "unavailable" : "error");
        }
        bump();
      });
    }, REQUOTE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, chosenSize, chosenUnits, chosenAt, sizeKey, count, token, language, admin]);

  // A price shown for another parcel is not for this one: cleared when it moves.
  useEffect(() => {
    setQuote((q) => (q && q.state === "ok" && (q.sizeKey !== sizeKey || q.units !== count) ? null : q));
  }, [sizeKey, count]);

  const pick: ExpressPick | undefined =
    enabled && chosen && !busy && fits(chosen)
      ? {
          country: chosen.at.country,
          city: chosen.at.city.trim(),
          postcode: chosen.at.postcode.trim(),
          line1: chosen.at.line1.trim(),
          name: chosen.at.name.trim().slice(0, 60),
          cents: chosen.cents,
        }
      : undefined;
  const hold = enabled && (open || busy || (!!chosen && !fits(chosen)));
  // The session is made for the address, the name, the price and the count:
  // the key moves with those and nothing else. While the choice is being
  // changed (hold), the last one stays, so choosing the same again rebuilds
  // nothing, and a free session is only made once free delivery is chosen.
  const key = pick
    ? `x:${pick.country}:${pick.postcode}:${pick.city}:${pick.line1}:${pick.name}:${pick.cents}:${count}${retries ? `:r${retries}` : ""}`
    : hold
      ? lastKey.current
      : "";
  lastKey.current = key;
  return {
    enabled,
    pick,
    hold,
    key,
    refused,
    sizeKey,
    units: count,
    open,
    asking,
    busy,
    errored,
    quote,
    notice,
    typed,
    chosen,
    language,
    currency,
    countries,
    setField,
    openIt,
    see,
    use,
    change,
    keepFree,
    retry,
  };
}

/** Wraps the pay form: hidden, not rebuilt, while the express fields are open.
 *  Where express is not priced live it is nothing at all, so the page is
 *  exactly the one it was. */
export function ExpressHold({ ex, children }: { ex: ExpressState; children: ReactNode }) {
  if (!ex.enabled) return <>{children}</>;
  return <div style={ex.hold ? { display: "none" } : undefined}>{children}</div>;
}

/** The country's name in the page's language, or its code where the browser
 *  cannot name it. */
function countryName(code: string, language: Language): string {
  try {
    return new Intl.DisplayNames([language === "zh-hans" ? "zh-Hans" : language], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** What the order comes to before express, in minor units and the format the
 *  page writes its prices with, so the quote can say the total it makes. Left
 *  out where the sheet cannot say it. */
export interface ExpressTotal {
  minor: number;
  format: string;
}

/** The express choice, above the pay form. Says nothing where the server does
 *  not price express live. `delivery` gives the dated express window (and, in
 *  the weeks express can still make Christmas, the sentence saying so), and
 *  `total` the order before express. */
export default function ExpressChoice({
  ex,
  words: t,
  delivery,
  total,
}: {
  ex: ExpressState;
  words: Words;
  delivery?: PreviewDoc["delivery"] | null;
  total?: ExpressTotal | null;
}) {
  const [missing, setMissing] = useState(false);
  // Every country the checkout ships to, by name in the page's language.
  const options = useMemo(() => {
    const locale = ex.language === "zh-hans" ? "zh-Hans" : ex.language;
    const named = ex.countries.map((code) => ({ code, name: countryName(code, ex.language) }));
    try {
      return named.sort((a, b) => a.name.localeCompare(b.name, locale));
    } catch {
      return named.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [ex.countries, ex.language]);
  if (!ex.enabled) return null;
  const { typed, chosen, quote } = ex;
  const optional = postcodeOptional(typed.country);
  // Said under the field itself once they have pressed the button.
  const empty = (key: keyof Typed) => missing && !typed[key].trim() && !(key === "postcode" && optional);
  const noCountry = missing && !typed.country;
  const field = (
    id: string,
    key: keyof Typed,
    label: string,
    auto: string,
    more: { inputMode?: "text" | "numeric"; capitalize?: "words" | "characters" | "none"; maxLength?: number; last?: boolean } = {}
  ) => (
    <>
      <label className="express-label" htmlFor={`ex-${id}`}>
        {label}
      </label>
      <input
        id={`ex-${id}`}
        className="field"
        name={`shipping-${id}`}
        autoComplete={`shipping ${auto}`}
        autoCapitalize={more.capitalize ?? "words"}
        autoCorrect="off"
        spellCheck={false}
        inputMode={more.inputMode ?? "text"}
        enterKeyHint={more.last ? "go" : "next"}
        maxLength={more.maxLength}
        aria-invalid={empty(key) || undefined}
        aria-describedby={empty(key) ? `ex-${id}-err` : undefined}
        value={typed[key]}
        onChange={(e) => ex.setField(key, e.target.value)}
      />
      {empty(key) && (
        <p id={`ex-${id}-err`} className="tiny" role="alert" style={{ color: "var(--red)", margin: "4px 0 0" }}>
          {t.t("expressFieldNeeded")}
        </p>
      )}
    </>
  );
  const notice =
    ex.notice === "repriced" ? t.t("expressRepriced") : ex.notice === "unavailable" ? t.t("expressUnavailable") : ex.notice === "error" ? t.t("expressError") : "";
  // The dated window the server worked out (and Christmas over it, only while
  // express can still make it), or the old undated line from a server that
  // sends none.
  const arrivesFree = delivery?.arrives;
  const arrivesExpress = delivery?.expressArrives || t.t("expressArrives");
  const christmas = delivery?.expressChristmas;
  const included = t.t(ex.currency === "USD" ? "expressIncludedUs" : "expressIncluded");
  const withExpress = (cents: number) => (total && total.format ? fill(total.format, total.minor + cents) : "");
  const express = (
    <>
      <div className="tiny muted">{arrivesExpress}</div>
      {christmas && <div className="tiny" style={{ fontWeight: 600 }}>{christmas}</div>}
    </>
  );

  if (ex.busy) return <div className="express-box" role="status">{<p className="small">{t.t("expressChecking")}</p>}</div>;

  if (chosen && !ex.open) {
    const sum = withExpress(chosen.cents);
    return (
      <>
        <div className="express-box express-row">
          <div className="small">
            <strong>{t.t("expressYours", { place: [chosen.at.postcode.trim(), chosen.at.city.trim()].filter(Boolean).join(", ") })}</strong> ·{" "}
            <strong className="mono">{chosen.price}</strong>
            <div className="tiny muted">{included}</div>
            {sum && <div className="tiny">{t.t("expressTotal", { total: sum })}</div>}
            {express}
          </div>
          <button type="button" className="btn btn-quiet btn-sm" onClick={ex.change} aria-label={`${t.t("expressChange")}: ${t.t("expressTitle")}`}>
            {t.t("expressChange")}
          </button>
        </div>
        {ex.errored && (
          <div className="express-box" role="alert">
            <p className="notice" style={{ margin: 0 }}>{t.t("expressError")}</p>
            <button type="button" className="btn btn-quiet btn-block" style={{ marginTop: 8 }} onClick={ex.retry}>
              {t.t("expressTryAgain")}
            </button>
          </div>
        )}
      </>
    );
  }

  if (!ex.open) {
    return (
      <div className="express-box express-row">
        <div className="small">
          <strong>{t.t("expressFree")}</strong>
          {arrivesFree && <div className="tiny muted">{arrivesFree}</div>}
          {notice && <div className="tiny" role="status" style={{ marginTop: 4 }}>{notice}</div>}
        </div>
        <button type="button" className="btn btn-soft btn-sm" onClick={ex.openIt}>
          {t.t("expressSooner")}
        </button>
      </div>
    );
  }

  return (
    <div className="express-box">
      <p className="express-title">{t.t("expressTitle")}</p>
      <p className="tiny muted" style={{ margin: "2px 0 0" }}>
        {t.t("expressIntro")}
      </p>
      {notice && <p className="notice" role="status" style={{ marginTop: 10 }}>{notice}</p>}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setMissing(true);
          if (!ex.asking) ex.see();
        }}
      >
        {field("name", "name", t.t("expressName"), "name", { maxLength: 60 })}
        <label className="express-label" htmlFor="ex-country">
          {t.t("expressCountry")}
        </label>
        <select
          id="ex-country"
          className="field"
          name="shipping-country"
          autoComplete="shipping country"
          aria-invalid={noCountry || undefined}
          aria-describedby={noCountry ? "ex-country-err" : undefined}
          value={typed.country}
          onChange={(e) => ex.setField("country", e.target.value)}
        >
          {!typed.country && <option value="">{t.t("expressCountryPick")}</option>}
          {options.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        {noCountry && (
          <p id="ex-country-err" className="tiny" role="alert" style={{ color: "var(--red)", margin: "4px 0 0" }}>
            {t.t("expressFieldNeeded")}
          </p>
        )}
        {field("street", "line1", t.t("expressStreet"), "address-line1", { maxLength: 120 })}
        {field("postcode", "postcode", optional ? t.t("expressPostcodeOptional") : t.t("expressPostcode"), "postal-code", {
          capitalize: "characters",
          maxLength: 20,
        })}
        {field("city", "city", t.t("expressCity"), "address-level2", { maxLength: 80, last: true })}
        {!quote && (
          <button type="submit" className="btn btn-primary btn-block" style={{ marginTop: 12 }} disabled={ex.asking}>
            {ex.asking ? t.t("expressChecking") : t.t("expressCheck")}
          </button>
        )}
      </form>
      <div role="status">
        {quote?.state === "ok" && (
          <div style={{ marginTop: 12 }}>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--ink)" }}>{t.t("expressPrice", { price: quote.price })}</p>
            <p className="tiny muted" style={{ margin: "2px 0 0" }}>{included}</p>
            {withExpress(quote.cents) && (
              <p className="small" style={{ margin: "2px 0 0", fontWeight: 600 }}>{t.t("expressTotal", { total: withExpress(quote.cents) })}</p>
            )}
            {quote.remote && <p className="small" style={{ margin: "2px 0 0" }}>{t.t("expressRemote")}</p>}
            <div style={{ margin: "2px 0 10px" }}>{express}</div>
            <button type="button" className="btn btn-primary btn-block" onClick={() => {
              setMissing(false);
              ex.use();
            }}>
              {t.t("expressUse", { price: quote.price })}
            </button>
          </div>
        )}
        {quote?.state === "none" && <p className="notice" style={{ marginTop: 12 }}>{t.t("expressNone")}</p>}
        {quote?.state === "error" && (
          <>
            <p className="notice" style={{ marginTop: 12 }}>{t.t("expressError")}</p>
            <button type="button" className="btn btn-quiet btn-block" onClick={ex.see}>
              {t.t("expressTryAgain")}
            </button>
          </>
        )}
      </div>
      <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 6 }} onClick={ex.keepFree}>
        {t.t("expressKeepFree")}
      </button>
    </div>
  );
}
